import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Cycle {
  id: number
  display_name: string
  supervised_by_id: number | null
}

interface Supervisor {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  cycle: Cycle
  supervisors: Supervisor[]
}

export default function AdminPerformanceReviewCycleEdit() {
  const { cycle, supervisors } = usePage<Props>().props

  const { data, setData, patch, processing, errors } = useForm({
    supervised_by_id: cycle.supervised_by_id ? String(cycle.supervised_by_id) : "",
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/admin/performance_review_cycles/${cycle.id}`)
  }

  return (
    <>
      <Head title={`Edit ${cycle.display_name} - Admin - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin">Admin</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/admin/performance_review_cycles/${cycle.id}`}>
                  {cycle.display_name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/admin/performance_review_cycles/${cycle.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit {cycle.display_name}</h1>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="supervised_by_id">Supervisor</Label>
                    <Select value={data.supervised_by_id} onValueChange={(v) => setData("supervised_by_id", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select supervisor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {supervisors.map((u) => (
                          <SelectItem key={u.id} value={String(u.id)}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href={`/admin/performance_review_cycles/${cycle.id}`}>Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Update Cycle"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

AdminPerformanceReviewCycleEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
